import React, { useState, useEffect } from 'react';
import {
  Users,
  TrendingUp,
  TrendingDown,
  Minus,
  UserCheck,
  ArrowRightLeft,
  RefreshCw
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import statisticsService from '../../services/statisticsService';
import StatCard from '../Dashboard/StatCard';
import Avatar from '../UI/Avatar';
import { Loader } from '../UI/LoadingSpinner';
import { TYPE_COLORS, CHART_CURSOR, ACTIVE_BAR, activeDot, ChartTooltip } from '../../utils/chartTheme';

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const StatisticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const now = new Date();
      const thisMonthStart = formatDate(new Date(now.getFullYear(), now.getMonth(), 1));
      const thisMonthEnd = formatDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
      const lastMonthStart = formatDate(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      const lastMonthEnd = formatDate(new Date(now.getFullYear(), now.getMonth(), 0));

      // 3 параллельных запроса
      const [managersResponse, comparisonResponse, trendResponse] = await Promise.all([
        statisticsService.getManagerTalentsAdded({
          startDate: thisMonthStart,
          endDate: thisMonthEnd,
          limit: 5
        }),
        statisticsService.getPeriodComparison({
          period1_start: lastMonthStart,
          period1_end: lastMonthEnd,
          period2_start: thisMonthStart,
          period2_end: thisMonthEnd
        }),
        statisticsService.getCustomReport({
          dimensions: ['created_month'],
          filters: {
            startDate: lastMonthStart,
            endDate: thisMonthEnd
          },
          limit: 12
        })
      ]);

      setDashboardData({
        managers: managersResponse,
        comparison: comparisonResponse,
        trend: trendResponse
      });
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const renderChangeIndicator = (change) => {
    if (!change) return null;
    const { absolute, percentage } = change;
    const isPositive = absolute > 0;
    const isNegative = absolute < 0;
    const IconComponent = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;
    const colorClass = isPositive
      ? 'text-green-600 dark:text-green-400'
      : isNegative
      ? 'text-red-600 dark:text-red-400'
      : 'text-gray-500 dark:text-gray-400';

    return (
      <span className={`flex items-center gap-1 text-sm font-medium ${colorClass}`}>
        <IconComponent className="h-3.5 w-3.5" />
        {absolute >= 0 ? '+' : ''}{absolute} ({percentage >= 0 ? '+' : ''}{percentage}%)
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader variant="component" size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
        {error}
        <button
          onClick={loadDashboard}
          className="ml-3 underline text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!dashboardData) return null;

  const { comparison, managers, trend } = dashboardData;
  const period2 = comparison?.period2 || {};
  const comparisonData = comparison?.comparison || {};

  // Conversions
  const toEmployee = period2.conversions?.to_employee || 0;
  const toPresale = period2.conversions?.to_presale || 0;
  const remainingCandidates = Math.max(0, (period2.by_type?.candidate || 0) - toEmployee - toPresale);

  // KPI данные
  const totalThisMonth = period2.total_talents || 0;
  const totalChange = comparisonData.total_talents || { absolute: 0, percentage: 0 };
  const candidatesCount = remainingCandidates;
  const candidatesChange = comparisonData.by_type?.candidate || { absolute: 0, percentage: 0 };
  const conversionsTotal = toEmployee + toPresale;
  const conversionsChange = {
    absolute: (comparisonData.conversions?.to_employee?.absolute || 0) + (comparisonData.conversions?.to_presale?.absolute || 0),
    percentage: 0
  };

  // Trend тренд для StatCard
  const totalTrendStr = totalChange.absolute >= 0
    ? `+${totalChange.percentage}%`
    : `${totalChange.percentage}%`;

  const pieData = [
    { name: 'Candidates', value: remainingCandidates, key: 'candidate' },
    { name: '→ Employees', value: toEmployee, key: 'employee' },
    { name: '→ Presales', value: toPresale, key: 'presale' }
  ].filter(item => item.value > 0);

  // Данные для Line chart (тренд по месяцам)
  const trendRows = trend?.rows || [];
  const chartData = trendRows
    .map(row => {
      const monthRaw = row.created_month || row.date || '';
      return { monthRaw, count: row.count || 0 };
    })
    .filter(row => row.monthRaw)
    .sort((a, b) => a.monthRaw.localeCompare(b.monthRaw))
    .map(row => ({
      date: new Date(row.monthRaw).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      count: row.count,
    }));

  // Топ менеджеры
  const topManagers = Array.isArray(managers)
    ? managers
    : (managers?.data || []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="break-words text-2xl font-bold text-gray-900 dark:text-gray-100">
            Statistics Dashboard
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Overview for {comparison?.period2?.label || 'current month'} vs {comparison?.period1?.label || 'last month'}
          </p>
        </div>
        <button
          onClick={loadDashboard}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Talents This Month"
          value={totalThisMonth}
          icon={Users}
          trend={totalTrendStr}
          trendLabel="vs last month"
          color="blue"
        />
        <StatCard
          title="Candidates This Month"
          value={candidatesCount}
          icon={Users}
          trend={candidatesChange.absolute >= 0 ? `+${candidatesChange.percentage}%` : `${candidatesChange.percentage}%`}
          trendLabel="vs last month"
          color="purple"
        />
        <StatCard
          title="Conversions This Month"
          value={conversionsTotal}
          icon={ArrowRightLeft}
          trend={conversionsChange.absolute >= 0 ? `+${conversionsChange.absolute}` : `${conversionsChange.absolute}`}
          trendLabel="vs last month"
          color="green"
        />
        <StatCard
          title="Active Managers"
          value={topManagers.length}
          icon={UserCheck}
          color="orange"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Trend Line Chart (2/3 ширины) */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Talent Addition Trend (by month)
          </h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250} minWidth={0}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                  className="fill-gray-500 dark:fill-gray-400"
                />
                <YAxis allowDecimals={false} className="fill-gray-500 dark:fill-gray-400" />
                <Tooltip cursor={CHART_CURSOR} content={<ChartTooltip />} />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke={TYPE_COLORS.candidate}
                  strokeWidth={2}
                  dot={{ r: 3, fill: TYPE_COLORS.candidate }}
                  activeDot={activeDot(TYPE_COLORS.candidate)}
                  name="Talents Added"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[250px] text-gray-400 dark:text-gray-500">
              No trend data available
            </div>
          )}
        </div>

        {/* Distribution Donut Chart (1/3 ширины) */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Distribution by Type
          </h3>
          {pieData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180} minWidth={0}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                  >
                    {pieData.map((entry) => (
                      <Cell key={entry.key} fill={TYPE_COLORS[entry.key]} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-2">
                {pieData.map((entry) => (
                  <div key={entry.key} className="flex items-center justify-between text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className="w-3 h-3 rounded-full inline-block"
                        style={{ backgroundColor: TYPE_COLORS[entry.key] }}
                      />
                      <span className="text-gray-600 dark:text-gray-400">{entry.name}</span>
                    </div>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{entry.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-[180px] text-gray-400 dark:text-gray-500">
              No data for this month
            </div>
          )}
        </div>
      </div>

      {/* Comparison Summary + Top Managers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Month Comparison Summary */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {comparison?.period2?.label || 'This Month'} vs {comparison?.period1?.label || 'Last Month'}
          </h3>
          <div className="space-y-3">
            {[
              {
                label: 'Total Talents',
                period1: comparison?.period1?.total_talents || 0,
                period2: comparison?.period2?.total_talents || 0,
                change: comparisonData.total_talents
              },
              {
                label: 'Candidates (remaining)',
                period1: Math.max(0,
                  (comparison?.period1?.by_type?.candidate || 0)
                  - (comparison?.period1?.conversions?.to_employee || 0)
                  - (comparison?.period1?.conversions?.to_presale || 0)
                ),
                period2: remainingCandidates,
                change: null
              },
              {
                label: '→ Employees (conversions)',
                period1: comparison?.period1?.conversions?.to_employee || 0,
                period2: comparison?.period2?.conversions?.to_employee || 0,
                change: comparisonData.conversions?.to_employee
              },
              {
                label: '→ Presales (conversions)',
                period1: comparison?.period1?.conversions?.to_presale || 0,
                period2: comparison?.period2?.conversions?.to_presale || 0,
                change: comparisonData.conversions?.to_presale
              }
            ].map((row, i) => (
              <div
                key={i}
                className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
              >
                <span className="text-sm text-gray-600 dark:text-gray-400">{row.label}</span>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-400 dark:text-gray-500 w-6 text-right">{row.period1}</span>
                  <span className="text-sm text-gray-300 dark:text-gray-600">→</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 w-6 text-right">{row.period2}</span>
                  <div className="w-32 text-right">
                    {renderChangeIndicator(row.change)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Managers */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Top Managers This Month
          </h3>
          {topManagers.length > 0 ? (
            <div className="space-y-3">
              {topManagers.slice(0, 5).map((item, index) => {
                const manager = item.manager;
                const total = item.total_talents || 0;
                return (
                  <div key={item.manager_id || index} className="flex min-w-0 items-center gap-3">
                    <span className="text-sm font-bold text-gray-400 dark:text-gray-500 w-5 text-center">
                      {index + 1}
                    </span>
                    {manager ? (
                      <Avatar
                        avatar={manager.avatar}
                        name={manager.fullName || manager.name || manager.email}
                        size="sm"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <Users className="h-4 w-4 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {manager?.fullName || manager?.name || manager?.email || 'Unknown'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {item.by_type?.candidate || 0} candidates
                        {item.by_type?.employee ? `, ${item.by_type.employee} employees` : ''}
                        {item.by_type?.presale ? `, ${item.by_type.presale} presales` : ''}
                      </p>
                    </div>
                    <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                      {total}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[180px] text-gray-400 dark:text-gray-500">
              <Users className="h-10 w-10 mb-2 opacity-40" />
              <p className="text-sm">No manager data for this month</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatisticsDashboard;
